import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import { ReferenceChip } from '@/components/ReferenceChip';
import { api } from '@/lib/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Printer, Download, Gem, Coins, RotateCcw, PackageX,
  PencilLine,
} from 'lucide-react';
import {
  formatINR, formatCarats, formatGrams, formatDateTime,
} from '@/lib/format';
import { extractError } from '@/lib/errors';
import MemoLink from '@/components/phase7/MemoLink';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import { RequestEditDialog } from '@/components/RequestEditDialog';
import EditedMarker from '@/components/EditedMarker';
import { EDITED_VALUE_CLASS, EDITED_ROW_CLASS } from '@/lib/editFlow';

// ============================================================================
// Goldsmith Return Memo page (route: /return-memos/{return_id})
// Mirrors the IssueMemo UX but with rose accents and "return" semantics.
// Used by the clickable "R-..." badges in OrderDetail, IssueMemo lifecycle,
// PacketDetail entries, and Gold ledger entries.
// ============================================================================
export default function GoldsmithReturnMemo() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <AppShell
      title="Return Memo"
      subtitle="Materials returned by the goldsmith. View / print / download."
      right={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9" data-testid="return-memo-back-button">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <Content id={id} />
    </AppShell>
  );
}

function Content({ id }) {
  const [memo, setMemo] = useState(null);
  const [pdfDialog, setPdfDialog] = useState({ open: false, mode: 'download' });
  const [brokenPdfDialog, setBrokenPdfDialog] = useState({ open: false, mode: 'download' });
  const [pendingEditId, setPendingEditId] = useState(null);

  const reload = () => {
    api.get(`/goldsmith-issues/returns/${id}`)
      .then(({ data }) => setMemo(data))
      .catch(() => setMemo({ error: true }));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  // Wave 3.51 — Detect any open edit request for this return memo so we
  // can show a lock banner and hide the Request Edit button until the
  // Super Admin resolves it.
  useEffect(() => {
    if (!memo || memo.error) return;
    api.get('/edit-requests/', { params: { role_scope: 'all' } })
      .then(({ data }) => {
        const open = (data || []).find(
          (r) =>
            r.entity_id === id &&
            r.entity_type === 'goldsmith_return' &&
            ['pending_sa', 'sent_back_to_user'].includes(r.status),
        );
        setPendingEditId(open ? open.id : null);
      })
      .catch(() => setPendingEditId(null));
  }, [id, memo?.last_edited_at]);

  if (!memo) return <div className="text-slate-500 py-8" data-testid="return-memo-loading">Loading…</div>;
  if (memo.error) return <div className="text-rose-600 py-8" data-testid="return-memo-not-found">Return memo not found.</div>;

  const lines = memo.lines || [];
  const topEdited = memo.edited_fields || {};
  const hasBroken = !!memo.has_broken_packet_return
    || lines.some((ln) => (ln.to_broken || {}).enabled);
  // Aggregate totals per material for the summary chip strip.
  const diamondLines = lines.filter((l) => (l.kind || l.material_type) === 'diamond');
  const goldLines = lines.filter((l) => (l.kind || l.material_type) === 'gold');
  const totalCt = diamondLines.reduce((s, l) => s + Math.abs(Number(l.carats || 0)), 0);
  const totalPcs = diamondLines.reduce((s, l) => s + Math.abs(Number(l.pieces || 0)), 0);
  const totalG = goldLines.reduce((s, l) => s + Math.abs(Number(l.grams || 0)), 0);
  const totalValue = lines.reduce((s, l) => s + Math.abs(Number(l.value || 0)), 0);
  const safeName = (memo.return_memo_no || memo.return_no || id || 'memo').replace(/\//g, '-');

  return (
    <div className="space-y-4">
      <Card className="p-5 border-l-4 border-l-rose-400" data-testid="return-memo-header">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-rose-700">
              <RotateCcw size={11} /> Return Memo No.
            </div>
            <div className="text-2xl font-mono font-semibold text-emerald-700" data-testid="return-memo-detail-no">
              {memo.return_memo_no || memo.return_no}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {formatDateTime(memo.created_at)} · by{' '}
              <span className="font-medium text-slate-700">{memo.created_by_email}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2 items-center">
              {totalCt > 0 && (
                <Badge className="bg-[var(--diamond-bg)] text-[var(--diamond-icon)] text-[10px]">
                  <Gem size={9} className="mr-1" /> {formatCarats(totalCt)} ct · {totalPcs} pcs
                </Badge>
              )}
              {totalG > 0 && (
                <Badge className="bg-[var(--gold-bg)] text-[var(--gold-icon)] text-[10px]">
                  <Coins size={9} className="mr-1" /> {formatGrams(totalG)} g
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">From Goldsmith</div>
            <Link
              to={`/goldsmiths/${memo.goldsmith_id}`}
              className="font-medium text-[var(--ink-navy)] hover:underline"
              data-testid="return-memo-goldsmith-link"
            >
              {memo.goldsmith_name}
            </Link>
            <div className="flex gap-2 mt-2 flex-wrap justify-end">
              <Button variant="outline" className="h-9" data-testid="return-memo-view-pdf" onClick={() => setPdfDialog({ open: true, mode: 'view' })}>
                <FileText size={14} className="mr-1.5" /> View PDF
              </Button>
              <Button variant="outline" className="h-9" data-testid="return-memo-print" onClick={() => setPdfDialog({ open: true, mode: 'print' })}>
                <Printer size={14} className="mr-1.5" /> Print
              </Button>
              <Button variant="outline" className="h-9" data-testid="return-memo-download-pdf" onClick={() => setPdfDialog({ open: true, mode: 'download' })}>
                <Download size={14} className="mr-1.5" /> Download
              </Button>
              {hasBroken && (
                <Button
                  className="h-9 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="return-memo-broken-voucher"
                  onClick={() => setBrokenPdfDialog({ open: true, mode: 'download' })}
                >
                  <PackageX size={14} className="mr-1.5" /> Broken Voucher
                </Button>
              )}
              {/* Wave 3.51 — Request Edit (CA/SA only, within T+1 IST window). */}
              {!pendingEditId && (
                <RequestEditDialog
                  entityType="goldsmith_return"
                  entity={memo}
                  testId="return-memo-request-edit"
                  onSuccess={() => { reload(); }}
                />
              )}
            </div>
          </div>
        </div>
        {pendingEditId && (
          <div
            className="mt-3 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2 rounded flex items-center gap-2"
            data-testid="return-memo-edit-pending-banner"
          >
            <PencilLine size={14} />
            <span>
              <strong>Property locked</strong> — a pending Edit Request blocks
              further movements on this Return Memo until the Super Admin acts.
            </span>
            <Link
              to="/edit-requests"
              className="ml-auto text-amber-900 underline hover:no-underline"
              data-testid="return-memo-edit-pending-link"
            >
              Open Edit Requests
            </Link>
          </div>
        )}
        {(memo.reference || memo.note) && (
          <div className="mt-3 text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded" data-testid="return-memo-meta">
            {memo.reference && (
              <span>
                <strong>Reference:</strong>{' '}
                <span className={topEdited.reference ? EDITED_VALUE_CLASS : ''}>
                  {memo.reference}
                </span>
                <EditedMarker show={!!topEdited.reference} />
              </span>
            )}
            {memo.reference && memo.note && <span> · </span>}
            {memo.note && (
              <span>
                <strong>Note:</strong>{' '}
                <span className={topEdited.note ? EDITED_VALUE_CLASS : ''}>
                  {memo.note}
                </span>
                <EditedMarker show={!!topEdited.note} />
              </span>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="text-base font-medium text-[var(--ink-navy)] mb-2">Line Items</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="return-memo-lines-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Material</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-right px-3 py-2">Quantity</th>
                <th className="text-right px-3 py-2">Value (₹)</th>
                <th className="text-left px-3 py-2">Source Issue</th>
                <th className="text-left px-3 py-2">Order</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const kind = l.kind || l.material_type;
                const isDiamond = kind === 'diamond';
                const lineEdited = l.edited_fields || {};
                const qtyEdited = !!(lineEdited.carats || lineEdited.pieces || lineEdited.grams);
                const noteEdited = !!lineEdited.note;
                return (
                  <tr
                    key={i}
                    className={`border-t border-[var(--divider)] ${
                      qtyEdited || noteEdited ? EDITED_ROW_CLASS : ''
                    }`}
                    data-testid={`return-memo-line-${i}`}
                  >
                    <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2">
                      {isDiamond
                        ? <Gem size={12} className="inline mr-1 text-[var(--diamond-icon)]" />
                        : <Coins size={12} className="inline mr-1 text-[var(--gold-icon)]" />}
                      <span className="capitalize">{kind}</span>
                    </td>
                    <td className="px-3 py-2">
                      {isDiamond ? (
                        l.packet_id ? (
                          <Link
                            to={`/diamonds/packets/${l.packet_id}`}
                            className="font-mono text-[12px] bg-[var(--diamond-bg)] text-[var(--diamond-icon)] px-2 py-0.5 rounded hover:underline"
                            data-testid={`return-memo-line-${i}-packet-link`}
                          >
                            {l.packet_no}
                          </Link>
                        ) : (
                          <span className="font-mono text-[12px]">{l.packet_no}</span>
                        )
                      ) : (
                        <span>{l.purity_name}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      <span className={qtyEdited ? EDITED_VALUE_CLASS : ''}>
                        {isDiamond
                          ? `${formatCarats(Math.abs(l.carats || 0))} ct · ${Math.abs(l.pieces || 0)} pcs`
                          : `${formatGrams(Math.abs(l.grams || 0))} g`}
                      </span>
                      <EditedMarker show={qtyEdited} />
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{formatINR(Math.abs(l.value || 0))}</td>
                    <td className="px-3 py-2">
                      {l.issue_memo_no || l.goldsmith_issue_no ? (
                        <MemoLink
                          memoNo={l.issue_memo_no || l.goldsmith_issue_no}
                          testId={`return-memo-line-${i}-source-issue`}
                        />
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {l.order_id ? (
                        <Link
                          to={`/orders/${l.order_id}`}
                          className="text-[var(--ink-navy)] hover:underline"
                          data-testid={`return-memo-line-${i}-order-link`}
                        >
                          {l.order_no || 'Order'}
                        </Link>
                      ) : l.source && l.source !== 'unattributed' ? (
                        <Link
                          to={`/orders/${l.source}`}
                          className="text-[var(--ink-navy)] hover:underline"
                          data-testid={`return-memo-line-${i}-source-order`}
                        >
                          Open order
                        </Link>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">Unattributed</Badge>
                      )}
                      {l.order_id && l.order_title && (
                        <div
                          className="mt-0.5 text-[11px] font-medium text-emerald-700"
                          data-testid={`return-memo-line-${i}-order-title`}
                        >
                          {l.order_title}
                        </div>
                      )}
                      {l.order_id && l.customer_name && (
                        <div
                          className="mt-0.5 text-[11px] text-slate-600"
                          data-testid={`return-memo-line-${i}-customer`}
                        >
                          <span className="text-slate-400">Customer:</span>{' '}
                          <span className="font-medium text-[var(--ink-navy)]">{l.customer_name}</span>
                        </div>
                      )}
                      {memo.uses_reference_numbers && l.reference_no != null && (
                        <div className="mt-0.5">
                          <ReferenceChip
                            referenceNo={l.reference_no}
                            className="inline-flex items-center rounded bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 text-[10px] font-mono tabular-nums"
                            testId={`return-memo-line-${i}-reference`}
                            title={`${memo.reference_label || 'Page No.'} ${l.reference_no} — open in Order Forms (new tab)`}
                          >
                            {(memo.reference_label || 'Page No.')} {l.reference_no}
                          </ReferenceChip>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-right mt-3 text-sm font-mono text-slate-700">
          Total returned value:{' '}
          <span className="text-[var(--ink-navy)] font-medium" data-testid="return-memo-total-value">
            {formatINR(totalValue)}
          </span>
        </div>
      </Card>

      <PdfDownloadDialog
        open={pdfDialog.open}
        onOpenChange={(o) => setPdfDialog({ ...pdfDialog, open: o })}
        title={pdfDialog.mode === 'view' ? 'View Return Memo PDF' : pdfDialog.mode === 'print' ? 'Print Return Memo' : 'Download Return Memo PDF'}
        description="Choose whether to print your company logo and name on this return memo."
        companyId={memo.company_id}
        endpoint={`/goldsmith-issues/returns/${id}/pdf`}
        filename={`ReturnMemo_${safeName}.pdf`}
        mode={pdfDialog.mode}
      />
      {hasBroken && (
        <PdfDownloadDialog
          open={brokenPdfDialog.open}
          onOpenChange={(o) => setBrokenPdfDialog({ ...brokenPdfDialog, open: o })}
          title="Download Broken-Packet Voucher"
          description="Distinct voucher listing the broken raw material returned by the goldsmith — kept (returned to broken packet), weight loss, and destination broken packet."
          companyId={memo.company_id}
          endpoint={`/goldsmith-issues/returns/${id}/broken-voucher/pdf`}
          filename={`GoldsmithReturnToBroken_${safeName}.pdf`}
          mode={brokenPdfDialog.mode}
        />
      )}
    </div>
  );
}
